import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, LinearProgress, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { AUTOMATION_COLORS, AUTOMATION_LABELS, automationPercent, coverageRowId, coverageSummary, filterCoverage } from './coverage';
import { useAsyncList } from './useAsyncList';
const formatDate = value => value ? new Date(value).toLocaleDateString() : '-';

/**
 * Which providers are automated, which are a person logging in, and how much work each one costs.
 *
 * A full outer join, so a provider appears whether Livable has it, the scraper has it, or both.
 * Manual providers lead, ordered by utilities, because that ordering is the automation backlog.
 */
export const ProviderCoverage = observer(() => {
  const store = useStore();
  const state = useAsyncList(store.admin.getBillScrapingProvidersAsync, () => store.admin.getBillScrapingProvidersAsync.run(), 'Could not load provider coverage.', [store]);
  const _useState = useState({
      automation: 'all',
      search: '',
      onlyActionRequired: false,
      onlyWithCredentials: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    filters = _useState2[0],
    setFilters = _useState2[1];
  const summary = useMemo(() => coverageSummary(state.rows), [state.rows]);
  const percent = useMemo(() => automationPercent(state.rows), [state.rows]);
  const visible = useMemo(() => filterCoverage(state.rows, filters), [state.rows, filters]);
  const columns = useMemo(() => [{
    field: 'name',
    headerName: 'Provider',
    flex: 1.4,
    minWidth: 200,
    isExportable: true,
    renderCell: _ref => {
      let row = _ref.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, row.name), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary"
      }, row.scraperProviderKey || 'no scraper key'));
    }
  }, {
    field: 'automation',
    headerName: 'Automation',
    width: 150,
    isExportable: true,
    renderCell: _ref2 => {
      let row = _ref2.row;
      return /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 0.5,
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        label: AUTOMATION_LABELS[row.automation] || row.automation,
        color: AUTOMATION_COLORS[row.automation] || 'default'
      }), row.walled ? /*#__PURE__*/React.createElement(Tooltip, {
        title: "Built, but blocked outside Livable: a login wall, a captcha score-gate, or a mandatory OTP."
      }, /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        label: "walled",
        variant: "outlined"
      })) : null);
    }
  }, {
    field: 'companies',
    headerName: 'Companies',
    width: 110,
    type: 'number',
    isExportable: true
  }, {
    field: 'properties',
    headerName: 'Properties',
    width: 110,
    type: 'number',
    isExportable: true
  }, {
    field: 'utilities',
    headerName: 'Utilities',
    width: 100,
    type: 'number',
    isExportable: true
  }, {
    field: 'credentialsStored',
    headerName: 'Credentials',
    width: 130,
    isExportable: true,
    renderCell: _ref3 => {
      let row = _ref3.row;
      return /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, row.credentialsStored, row.credentialsNeedingAttention ? /*#__PURE__*/React.createElement(Typography, {
        component: "span",
        variant: "caption",
        color: "error"
      }, " (".concat(row.credentialsNeedingAttention, " need a person)")) : null);
    },
    valueGetterForExport: _ref4 => {
      let row = _ref4.row;
      return "".concat(row.credentialsStored, "/").concat(row.credentialsNeedingAttention, " need a person");
    }
  }, {
    field: 'lastRunAt',
    headerName: 'Last run',
    width: 130,
    isExportable: true,
    // Never run and ran-with-no-bills are different problems; a zero would merge them.
    renderCell: _ref5 => {
      let row = _ref5.row;
      return row.lastRunAt ? formatDate(row.lastRunAt) : 'never';
    },
    valueGetterForExport: _ref6 => {
      let row = _ref6.row;
      return row.lastRunAt ? formatDate(row.lastRunAt) : 'never';
    }
  }, {
    field: 'lastStatus',
    headerName: 'Last status',
    width: 130,
    isExportable: true,
    renderCell: _ref7 => {
      let row = _ref7.row;
      return row.lastStatus ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        label: row.lastStatus,
        color: row.actionRequired ? 'error' : 'default'
      }) : '-';
    },
    valueGetterForExport: _ref8 => {
      let row = _ref8.row;
      return row.lastStatus || '-';
    }
  }, {
    field: 'billsLastRun',
    headerName: 'Bills',
    width: 90,
    type: 'number',
    isExportable: true
  }, {
    field: 'dueNow',
    headerName: 'Due now',
    width: 160,
    isExportable: true,
    // The anti-hammer ledger, which had no reader anywhere until this column.
    renderCell: _ref9 => {
      let row = _ref9.row;
      return row.streams ? /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, "".concat(row.dueNow, " of ").concat(row.streams)), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary"
      }, row.nextWave ? "next wave ".concat(row.nextWave) : 'nothing parked')) : '-';
    },
    valueGetterForExport: _ref0 => {
      let row = _ref0.row;
      return row.streams ? "".concat(row.dueNow, "/").concat(row.streams) : '-';
    }
  }], []);
  if (state.loading) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        padding: '32px',
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(CircularProgress, null));
  }
  if (state.error) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      sx: {
        marginTop: '16px'
      }
    }, state.error);
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    sx: {
      marginBottom: '16px'
    }
  }, [{
    label: 'Providers in flight',
    value: summary.inFlight
  }, {
    label: 'Scrapers built',
    value: summary.built
  }, {
    label: 'Automated',
    value: summary.automated
  }, {
    label: 'Utilities done by hand',
    value: summary.manualUtilities
  }].map(tile => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 3,
    key: tile.label
  }, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, tile.label), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, tile.value))))), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px',
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    gutterBottom: true
  }, "A scraper covers ".concat(percent, "% of the utilities in flight (").concat(summary.automatedUtilities.toLocaleString(), " of ").concat((summary.automatedUtilities + summary.manualUtilities).toLocaleString(), ").")), /*#__PURE__*/React.createElement(LinearProgress, {
    variant: "determinate",
    value: percent,
    sx: {
      height: 10,
      borderRadius: 5
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    sx: {
      display: 'block',
      marginTop: '8px'
    },
    color: "text.secondary"
  }, "".concat(summary.built, " scrapers are built: ").concat(summary.automated, " carrying utilities, ").concat(summary.scraperIdle, " with nothing routed to them yet. A further ").concat(summary.dormant.toLocaleString(), " provider rows have no properties using them at all."))), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 2,
    sx: {
      marginBottom: '16px'
    },
    flexWrap: "wrap",
    useFlexGap: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Automation",
    value: filters.automation,
    onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
      automation: e.target.value
    })),
    sx: {
      minWidth: 180
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: "all"
  }, "All"), Object.entries(AUTOMATION_LABELS).map(_ref1 => {
    let _ref10 = _slicedToArray(_ref1, 2),
      value = _ref10[0],
      label = _ref10[1];
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value
    }, label);
  })), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search provider",
    value: filters.search,
    onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
      search: e.target.value
    })),
    sx: {
      minWidth: 220
    }
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.onlyActionRequired,
      onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
        onlyActionRequired: e.target.checked
      }))
    }),
    label: "Needs a person"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.onlyWithCredentials,
      onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
        onlyWithCredentials: e.target.checked
      }))
    }),
    label: "Has credentials"
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns,
      rows: visible,
      name: 'provider-coverage.csv'
    })
  }, "Export CSV")), /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    columns: columns,
    rows: visible,
    getRowId: coverageRowId,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  })));
});